import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import {
    FileText, Eye, Save, Send, Trash2, RefreshCw, Search,
    Image as ImageIcon, Hash, Type, Globe, ChevronDown, ChevronRight, Video,
} from "lucide-react";

/**
 * CMS Admin Tab (Phase 1) — texts/numbers/images/videos editable from admin
 * with draft → preview → publish workflow and ES / EN support.
 *
 * Sections are derived from the block.section field. Each block is editable
 * in two language tabs. "Guardar borrador" saves but does not publish.
 * "Publicar" pushes the draft to live values. "Previsualizar todo" opens
 * the public site with ?preview=1 to render unpublished drafts.
 */
export default function AdminContentTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [showOnlyDrafts, setShowOnlyDrafts] = useState(false);
    const { reloadContent } = useI18n();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/content");
            setItems(data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al cargar contenido");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const draftCount = useMemo(() => items.filter(x => x.has_draft).length, [items]);

    const grouped = useMemo(() => {
        const q = filter.trim().toLowerCase();
        const filtered = items.filter(b => {
            if (showOnlyDrafts && !b.has_draft) return false;
            if (!q) return true;
            return (
                b.key.toLowerCase().includes(q) ||
                (b.value_es || "").toLowerCase().includes(q) ||
                (b.value_en || "").toLowerCase().includes(q) ||
                (b.draft_es || "").toLowerCase().includes(q) ||
                (b.draft_en || "").toLowerCase().includes(q)
            );
        });
        const m = {};
        for (const b of filtered) {
            (m[b.section] ||= []).push(b);
        }
        return m;
    }, [items, filter, showOnlyDrafts]);

    const onPublishAll = async () => {
        if (!confirm(`¿Publicar los ${draftCount} borradores ahora? Esta acción es inmediata para todos los usuarios.`)) return;
        try {
            const { data } = await api.post("/admin/content/publish-all");
            toast.success(`${data.published} bloque(s) publicado(s).`);
            await load();
            await reloadContent();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al publicar todo");
        }
    };

    const onPreviewAll = () => {
        // Opens public home in preview mode in a new tab. Backend gates this with admin cookie.
        window.open("/?preview=1", "_blank", "noopener");
    };

    return (
        <div className="space-y-5" data-testid="admin-content-tab">
            <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-p2w-gray" />
                        <Input
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Buscar por clave o texto…"
                            className="bg-p2w-elevated border-p2w-line text-white pl-9 w-72"
                            data-testid="content-filter"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-p2w-gray cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={showOnlyDrafts}
                            onChange={(e) => setShowOnlyDrafts(e.target.checked)}
                            className="accent-p2w-magenta"
                            data-testid="content-filter-drafts-only"
                        />
                        Sólo con borrador {draftCount > 0 && <span className="text-p2w-magenta font-semibold">({draftCount})</span>}
                    </label>
                    <Button variant="outline" size="sm" onClick={load} className="border-p2w-line bg-p2w-elevated text-white">
                        <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Recargar
                    </Button>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={onPreviewAll}
                        data-testid="content-preview-all"
                        className="border-p2w-purple/40 bg-p2w-purple/5 text-p2w-purple hover:bg-p2w-purple/15"
                    >
                        <Eye className="w-4 h-4 mr-1.5" /> Previsualizar todo
                    </Button>
                    <Button
                        onClick={onPublishAll}
                        disabled={draftCount === 0}
                        data-testid="content-publish-all"
                        className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold disabled:opacity-40"
                    >
                        <Send className="w-4 h-4 mr-1.5" /> Publicar todo {draftCount > 0 && `(${draftCount})`}
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="text-center text-p2w-gray py-12">Cargando contenido…</div>
            ) : Object.keys(grouped).length === 0 ? (
                <div className="text-center text-p2w-gray py-12 border border-dashed border-p2w-line rounded-xl">
                    Sin resultados.
                </div>
            ) : (
                <div className="space-y-4">
                    {Object.entries(grouped)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([section, blocks]) => (
                            <Section
                                key={section}
                                title={section}
                                blocks={blocks}
                                onReload={async () => { await load(); await reloadContent(); }}
                            />
                        ))}
                </div>
            )}
        </div>
    );
}

const SECTION_META = {
    home: { label: "Home", color: "text-p2w-green", emoji: "🏠" },
    footer: { label: "Footer", color: "text-p2w-purple", emoji: "📄" },
};

function Section({ title, blocks, onReload }) {
    const [open, setOpen] = useState(true);
    const meta = SECTION_META[title] || { label: title, color: "text-white" };
    const draftCount = blocks.filter(b => b.has_draft).length;

    return (
        <div className="rounded-xl border border-p2w-line bg-p2w-surface overflow-hidden">
            <button
                onClick={() => setOpen(!open)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-p2w-elevated/50 transition"
                data-testid={`content-section-toggle-${title}`}
            >
                <div className="flex items-center gap-3">
                    {open ? <ChevronDown className="w-4 h-4 text-p2w-gray" /> : <ChevronRight className="w-4 h-4 text-p2w-gray" />}
                    <span className={`font-sora font-bold uppercase tracking-wider text-sm ${meta.color}`}>
                        {meta.label}
                    </span>
                    <span className="text-xs text-p2w-gray">({blocks.length})</span>
                    {draftCount > 0 && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-p2w-magenta/15 text-p2w-magenta">
                            {draftCount} borrador{draftCount > 1 ? "es" : ""}
                        </span>
                    )}
                </div>
            </button>
            {open && (
                <div className="divide-y divide-p2w-line/60">
                    {blocks.map((b) => <BlockEditor key={b.key} block={b} onReload={onReload} />)}
                </div>
            )}
        </div>
    );
}

const TYPE_META = {
    text:      { icon: Type,       label: "Texto",  long: false },
    richtext:  { icon: FileText,   label: "Párrafo",long: true  },
    number:    { icon: Hash,       label: "Número", long: false },
    image_url: { icon: ImageIcon,  label: "Imagen", long: false },
    video_url: { icon: Video,      label: "Video",  long: false },
};

function BlockEditor({ block, onReload }) {
    const [lang, setLang] = useState("es");
    const [draftEs, setDraftEs] = useState(block.draft_es ?? block.value_es ?? "");
    const [draftEn, setDraftEn] = useState(block.draft_en ?? block.value_en ?? "");
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);

    useEffect(() => {
        setDraftEs(block.draft_es ?? block.value_es ?? "");
        setDraftEn(block.draft_en ?? block.value_en ?? "");
    }, [block.draft_es, block.draft_en, block.value_es, block.value_en]);

    const meta = TYPE_META[block.type] || TYPE_META.text;
    const Icon = meta.icon;

    const dirtyEs = draftEs !== (block.draft_es ?? block.value_es ?? "");
    const dirtyEn = draftEn !== (block.draft_en ?? block.value_en ?? "");
    const dirty = dirtyEs || dirtyEn;

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/content/${encodeURIComponent(block.key)}`, {
                draft_es: draftEs,
                draft_en: draftEn,
            });
            toast.success("Borrador guardado");
            await onReload();
            return true;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al guardar");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const publish = async () => {
        if (dirty) {
            // Auto-save current edits before publish; abort if save fails so we never publish stale state.
            const ok = await save();
            if (!ok) return;
        }
        setPublishing(true);
        try {
            await api.post(`/admin/content/${encodeURIComponent(block.key)}/publish`);
            toast.success(`Publicado: ${block.key}`);
            await onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al publicar");
        } finally {
            setPublishing(false);
        }
    };

    const discard = async () => {
        if (!confirm(`Descartar borrador de ${block.key}?`)) return;
        try {
            await api.post(`/admin/content/${encodeURIComponent(block.key)}/discard`);
            toast.success("Borrador descartado");
            await onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al descartar");
        }
    };

    const currentValue = lang === "es" ? draftEs : draftEn;
    const setCurrentValue = lang === "es" ? setDraftEs : setDraftEn;
    const publishedValue = lang === "es" ? block.value_es : block.value_en;

    return (
        <div className="p-4 hover:bg-p2w-elevated/30 transition" data-testid={`content-block-${block.key}`}>
            <div className="flex items-start gap-3 flex-wrap mb-3">
                <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-p2w-gray" />
                    <code className="font-mono text-xs text-white bg-p2w-elevated px-2 py-1 rounded">{block.key}</code>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-p2w-gray px-2 py-1 rounded bg-p2w-bg/50">
                    {meta.label}
                </span>
                {block.has_draft && (
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-p2w-magenta px-2 py-1 rounded bg-p2w-magenta/10">
                        ● Borrador sin publicar
                    </span>
                )}
                <span className="text-[10px] text-p2w-gray ml-auto">v{block.version}</span>
            </div>

            {/* Language switcher */}
            <div className="flex items-center gap-2 mb-2">
                {["es", "en"].map((l) => (
                    <button
                        key={l}
                        onClick={() => setLang(l)}
                        data-testid={`content-block-${block.key}-lang-${l}`}
                        className={`px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider transition border ${
                            lang === l
                                ? "bg-p2w-purple/20 border-p2w-purple text-p2w-purple"
                                : "bg-p2w-elevated border-p2w-line text-p2w-gray hover:text-white"
                        }`}
                    >
                        <Globe className="w-3 h-3 mr-1 inline" /> {l}
                    </button>
                ))}
            </div>

            {/* Editor */}
            {meta.long ? (
                <Textarea
                    value={currentValue}
                    onChange={(e) => setCurrentValue(e.target.value)}
                    placeholder={publishedValue}
                    rows={3}
                    className="bg-p2w-elevated border-p2w-line text-white font-mono text-sm"
                    data-testid={`content-block-${block.key}-input`}
                />
            ) : (
                <Input
                    value={currentValue}
                    onChange={(e) => setCurrentValue(e.target.value)}
                    placeholder={publishedValue}
                    type={block.type === "number" ? "number" : "text"}
                    className="bg-p2w-elevated border-p2w-line text-white"
                    data-testid={`content-block-${block.key}-input`}
                />
            )}
            {/* Image / Video preview */}
            {block.type === "image_url" && currentValue && (
                <div className="mt-2">
                    <img src={currentValue} alt="" className="max-h-32 rounded border border-p2w-line" />
                </div>
            )}

            {/* Current published reference */}
            {publishedValue && publishedValue !== currentValue && (
                <p className="text-[10px] text-p2w-gray mt-2 italic">
                    Publicado actualmente ({lang.toUpperCase()}):
                    <span className="ml-1 text-p2w-gray/80">&ldquo;{publishedValue.length > 100 ? publishedValue.slice(0, 100) + "…" : publishedValue}&rdquo;</span>
                </p>
            )}

            <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Button
                    size="sm"
                    onClick={save}
                    disabled={saving || !dirty}
                    data-testid={`content-block-${block.key}-save`}
                    className="bg-p2w-purple text-white hover:bg-p2w-purple/90 disabled:opacity-40 h-8"
                >
                    <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Guardando…" : "Guardar borrador"}
                </Button>
                <Button
                    size="sm"
                    onClick={publish}
                    disabled={publishing || (!block.has_draft && !dirty)}
                    data-testid={`content-block-${block.key}-publish`}
                    className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold disabled:opacity-40 h-8"
                >
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Publicar
                </Button>
                {block.has_draft && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={discard}
                        data-testid={`content-block-${block.key}-discard`}
                        className="border-p2w-line bg-p2w-elevated text-p2w-gray hover:text-white h-8"
                    >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Descartar
                    </Button>
                )}
            </div>
        </div>
    );
}
